import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React, { useCallback, useId, useRef } from "react";
import DateTimePicker from "~/components/forms/DateTimePicker";
import Select from "~/components/forms/dropdowns/SelectWrapper/Select";
import { editableValue } from "./editableValue";
import HelpTip from "./HelpTip";
import { NUMERIC_INPUT_HINTS, NUMERIC_TYPES, SELECT_TYPES } from "./types";

/** Shared controlled editor for definitions, Add Lead and Additional Details. */
const CustomFieldControl = ({
  field,
  value,
  onChange,
  disabled = false,
  error = null,
  id,
  menuPlacement = "bottom",
}) => {
  const generatedId = useId().replace(/:/g, "");
  const inputId = id || `custom-field-${generatedId}`;
  const errorId = `${inputId}-error`;
  const type = field.field_type;
  const hint = NUMERIC_INPUT_HINTS[type];
  const hintId = `${inputId}-hint`;
  const counterId = `${inputId}-counter`;
  const describedBy =
    [
      hint ? hintId : null,
      type === "long_text" || type === "short_text" ? counterId : null,
      error ? errorId : null,
    ]
      .filter(Boolean)
      .join(" ") || undefined;
  const common = {
    id: inputId,
    disabled,
    className: `form-control${error ? " is-invalid" : ""}`,
    "aria-required": !!field.required,
    "aria-invalid": !!error,
    "aria-describedby": describedBy,
  };
  const changeText = (event) =>
    onChange(event.target.value === "" ? null : event.target.value);
  const currentChange = useRef({ disabled, onChange });
  currentChange.current = { disabled, onChange };
  // DateTimePicker subscribes once: read the latest save lock and change handler.
  const changeDate = useCallback((date) => {
    if (!currentChange.current.disabled) {
      currentChange.current.onChange(date ? date.format("YYYY-MM-DD") : null);
    }
  }, []);
  let control;

  if (type === "boolean" || SELECT_TYPES.includes(type)) {
    const multiple = type === "multi_select";
    const options =
      type === "boolean"
        ? [
            { value: "unset", label: "Unset" },
            { value: "true", label: "Yes" },
            { value: "false", label: "No" },
          ]
        : (field.options || [])
            .filter((option) => !option.archived)
            .map((option) => ({
              value: String(option.id),
              label: option.label,
            }));
    let selectedValue = type === "boolean" ? "unset" : null;
    const available = editableValue(field, value);
    if (multiple) {
      selectedValue = (available || []).map(String);
    } else if (available != null) {
      selectedValue = String(available);
    }
    control = (
      <Select
        id={inputId}
        name={inputId}
        value={selectedValue}
        options={options}
        isMulti={multiple}
        isClearable={type !== "boolean"}
        isSearchable={type !== "boolean"}
        isDisabled={disabled}
        isInvalid={!!error}
        aria-required={!!field.required}
        aria-invalid={!!error}
        aria-describedby={describedBy}
        placeholder={multiple ? "Select options" : "Unset"}
        menuPosition="fixed"
        menuPlacement={menuPlacement}
        onChange={(selected) => {
          if (multiple) {
            onChange((selected || []).map((option) => option.value));
          } else if (type === "boolean") {
            onChange(
              !selected || selected.value === "unset"
                ? null
                : selected.value === "true",
            );
          } else {
            onChange(selected?.value ?? null);
          }
        }}
      />
    );
  } else if (type === "date") {
    control = (
      <DateTimePicker
        id={inputId}
        pickerType="date"
        format="YYYY-MM-DD"
        value={value || ""}
        isDisabled={disabled}
        isInvalid={!!error}
        showClear
        preserveInvalidInput
        onChange={changeDate}
      >
        <div
          className="input-group"
          onClickCapture={(event) => {
            if (disabled) {
              event.stopPropagation();
            }
          }}
        >
          <input
            {...common}
            type="text"
            placeholder="YYYY-MM-DD"
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                event.currentTarget.click();
              }
            }}
            value={value ?? ""}
            onChange={changeText}
          />
          <div className="input-group-append">
            <span className="input-group-text" aria-hidden="true">
              <FontAwesomeIcon icon={["far", "calendar-alt"]} />
            </span>
          </div>
        </div>
      </DateTimePicker>
    );
  } else if (type === "long_text") {
    control = (
      <textarea
        {...common}
        rows={4}
        maxLength={1000}
        value={value ?? ""}
        onChange={changeText}
      />
    );
  } else {
    control = (
      <input
        {...common}
        type={type === "date" ? "date" : "text"}
        inputMode={NUMERIC_TYPES.includes(type) ? "decimal" : undefined}
        maxLength={type === "short_text" ? 255 : undefined}
        value={value ?? ""}
        onChange={changeText}
      />
    );
  }

  return (
    <div className="form-group mb15">
      <label htmlFor={inputId} className="text-grey-dark">
        {field.label}
        {field.required && <span aria-hidden="true"> *</span>}
      </label>
      {field.help_text && (
        <HelpTip label={field.label}>{field.help_text}</HelpTip>
      )}
      {type === "currency_usd" ? (
        <div className="input-group">
          <div className="input-group-prepend">
            <span className="input-group-text" aria-hidden="true">
              $
            </span>
          </div>
          {control}
        </div>
      ) : (
        control
      )}
      {(type === "short_text" || type === "long_text") && (
        <small id={counterId} className="text-muted">
          {String(value ?? "").length} /{" "}
          {type === "short_text" ? "255" : "1,000"}
        </small>
      )}
      {hint && (
        <small id={hintId} className="form-text text-muted">
          {hint}
        </small>
      )}
      {error && (
        <div id={errorId} role="alert" className="invalid-feedback d-block">
          {error}
        </div>
      )}
    </div>
  );
};

CustomFieldControl.propTypes = {
  field: PropTypes.object.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.array,
  ]),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  id: PropTypes.string,
  menuPlacement: PropTypes.oneOf(["auto", "bottom", "top"]),
};

export default CustomFieldControl;
